"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";

export interface TestimonialType {
    _id?: string;
    id?: number | string;
    image?: string;
    text: string;
    clientName: string;
    clientAvatar?: string;
    rating: number;
}



export default function SocialProof({ data }: { data?: TestimonialType[] }) {
    const [activeIndex, setActiveIndex] = useState(0);
    const testimonials = data || [];

    const handlePrev = () => {
        setActiveIndex((current) => (current === 0 ? testimonials.length - 1 : current - 1));
    };

    const handleNext = () => {
        setActiveIndex((current) => (current + 1) % testimonials.length);
    };

    useEffect(() => {
        if (testimonials.length === 0) return;
        const interval = setInterval(() => {
            setActiveIndex((current) => (current + 1) % testimonials.length);
        }, 5000); // 5 seconds per slide

        return () => clearInterval(interval);
    }, [testimonials.length]);

    // If no testimonials exist, hide the section
    if (testimonials.length === 0) {
        return null;
    }

    return (
        <div className="w-full px-[5%] py-6 sm:py-[2vw] flex flex-col items-center relative z-10" id="testimonials">
            <h2 className="text-3xl sm:text-[3vw] font-bold mb-12 sm:mb-[4vw] text-center bg-linear-to-r from-[#B8BBBF] to-[#0073FF] bg-clip-text text-transparent">
                Social Proof
            </h2>

            <div className="w-full max-w-6xl mx-auto flex flex-col items-center">
                <div className="w-full relative group flex items-center justify-center">
                    <button
                        onClick={handlePrev}
                        className="absolute left-5 sm:-left-12 lg:-left-16 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-[2.5vw] md:h-[2.5vw] rounded-xl bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm flex items-center justify-center transition-all z-20"
                        aria-label="Previous slide"
                    >
                        <ChevronLeft className="w-6 h-6 md:w-[1.2vw] md:h-[1.2vw]" />
                    </button>

                    <div className="w-full grid grid-cols-1 grid-rows-1 relative">
                        {testimonials.map((testimonial, idx) => {
                            const isActive = activeIndex === idx;
                            const isPrev = idx === (activeIndex === 0 ? testimonials.length - 1 : activeIndex - 1);

                            let xOffset = 0;
                            if (!isActive) {
                                xOffset = isPrev ? -20 : 20;
                            }

                            return (
                                <motion.div
                                    key={idx}
                                    initial={false}
                                    animate={{
                                        opacity: isActive ? 1 : 0,
                                        x: xOffset,
                                        zIndex: isActive ? 10 : 0,
                                    }}
                                    transition={{ duration: 0.5, ease: "easeInOut" }}
                                    className={`col-start-1 row-start-1 w-full h-full flex flex-col sm:flex-row items-center sm:items-stretch justify-center gap-6 sm:gap-8 lg:gap-12 ${!isActive ? 'pointer-events-none' : ''}`}
                                    aria-hidden={!isActive}
                                >
                                    {/* Left Image */}
                                    <div className="w-full sm:w-auto flex items-center justify-center shrink-0">
                                        <div className="w-[280px] h-[280px] sm:w-[320px] sm:h-[320px] bg-linear-to-t from-[#3E89FE] via-transparent to-[#F82CD5] bg-black shrink-0 relative overflow-hidden rounded-xl">
                                            <Image
                                                src={testimonial.image || testimonial.clientAvatar || "/socialproof.jpg"}
                                                alt={testimonial.clientName || "Event Photo"}
                                                fill
                                                className="object-cover p-[1px] rounded-xl"
                                            />
                                        </div>
                                    </div>

                                    {/* Right Testimonial Card */}
                                    <div className="w-full sm:flex-1 flex shrink-0">
                                        {/* Gradient Border Wrapper */}
                                        <div className="w-full rounded-xl p-[1px] bg-linear-to-r from-[#0073FF]/50 via-transparent to-[#CA00F7]/50">
                                            <div className="w-full h-full bg-[#0a0410] rounded-xl p-6 sm:p-[2vw] flex flex-col justify-between">

                                                {/* Top row: Quotes & Rating */}
                                                <div className="flex justify-between items-start">
                                                    {/* Quote Icon */}
                                                    <svg width="40" height="34" viewBox="0 0 40 34" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white/40 w-8 h-8 sm:w-10 sm:h-10">
                                                        <path d="M12.9833 33.6C5.81663 33.6 0 27.7834 0 20.6167C0 12.0167 6.45 3.38335 15.6833 0.166687L17.75 4.31669C11.55 6.78335 8.1 12.1 7.78333 17.5834H12.9833C17.4166 17.5834 21.0333 21.2 21.0333 25.6334C21.0333 30.0667 17.4166 33.6 12.9833 33.6ZM31.95 33.6C24.7833 33.6 18.9667 27.7834 18.9667 20.6167C18.9667 12.0167 25.4167 3.38335 34.65 0.166687L36.7167 4.31669C30.5167 6.78335 27.0667 12.1 26.75 17.5834H31.95C36.3833 17.5834 40 21.2 40 25.6334C40 30.0667 36.3833 33.6 31.95 33.6Z" stroke="currentColor" strokeWidth="1" fill="none" />
                                                    </svg>

                                                    {/* Rating */}
                                                    <div className="bg-[#3E1A47] px-1.5 py-1 rounded-full flex gap-0.5 items-center">
                                                        {[...Array(testimonial.rating)].map((_, i) => (
                                                            <Star key={i} className="w-2.5 h-2.5 fill-[#E576FF] text-[#E576FF] drop-shadow-[0_0_10px_#E576FF]" />
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Text */}
                                                <p className="text-[#E2E8F0] text-sm sm:text-[1.1vw] leading-relaxed py-2 sm:py-[1vw]">
                                                    {testimonial.text}
                                                </p>

                                                {/* Client Info */}
                                                <div className="flex items-center gap-3">
                                                    <span className="text-[#FF66D9] font-medium text-sm sm:text-[1.2vw]">
                                                        {testimonial.clientName}
                                                    </span>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>

                    <button
                        onClick={handleNext}
                        className="absolute right-5 sm:-right-12 lg:-right-16 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-[2.5vw] md:h-[2.5vw] rounded-xl bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm flex items-center justify-center transition-all"
                        aria-label="Next slide"
                    >
                        <ChevronRight className="w-6 h-6 md:w-[1.2vw] md:h-[1.2vw]" />
                    </button>
                </div>
            </div>
        </div>
    );
}



